import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Edit, CheckCircle, Truck, XCircle, FileText, Calendar, User, Package, CreditCard } from 'lucide-react';
import { purchaseService } from '../services/purchaseService';
import { PurchaseResponseDTO } from '../types/purchase';
import { alertService } from '../services/alertService';
import { paymentService } from '../services/paymentService';
import { PaymentResponseDTO } from '../types/payment';

const PurchaseViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [loading, setLoading] = useState(true);
  const [purchase, setPurchase] = useState<PurchaseResponseDTO | null>(null);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [payments, setPayments] = useState<PaymentResponseDTO[]>([]);

  useEffect(() => {
    if (id) {
      loadPurchase();
    }
  }, [id]);

  const loadPurchase = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await purchaseService.getById(id);
      
      if (response.success && response.data) {
        setPurchase(response.data);
      } else {
        alertService.toast('error', 'Compra não encontrada.');
        navigate('/compras');
      }
    } catch (error) {
      console.error('Erro ao carregar compra:', error);
      alertService.toast('error', 'Erro ao carregar compra.');
      navigate('/compras');
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    if (!id) return;
    try {
      setPaymentsLoading(true);
      // Usar a rota dedicada por compra para garantir filtro correto
      const response = await paymentService.listByPurchasePath(id);
      if (response.success && response.data) {
        const raw = response.data as any;
        const items: PaymentResponseDTO[] = Array.isArray(raw) ? raw : (raw.content || []);
        setPayments(items);
      } else {
        setPayments([]);
      }
    } catch (error) {
      console.error('Erro ao carregar pagamentos:', error);
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadPayments();
    }
  }, [id]);

  const isPaidStatus = (status?: string) => {
    return ['EFETUADO', 'PAGO'].includes(String(status || '').toUpperCase());
  };

  const handleEdit = () => {
    navigate(`/compras/${id}`);
  };

  const handleStatusChange = async (action: 'confirm' | 'deliver' | 'cancel') => {
    if (!id || !purchase) return;

    try {
      let response;
      let message = '';
      
      switch (action) {
        case 'confirm':
          response = await purchaseService.confirm(id);
          message = 'Compra confirmada com sucesso!';
          break;
        case 'deliver':
          response = await purchaseService.markAsDelivered(id);
          message = 'Compra marcada como entregue!';
          break;
        case 'cancel':
          if (window.confirm('Tem certeza que deseja cancelar esta compra?')) {
            const motivo = window.prompt('Informe o motivo do cancelamento:', 'Cancelamento por motivo operacional');
            if (!motivo || motivo.trim() === '') {
              alertService.toast('warning', 'Motivo do cancelamento é obrigatório.');
              return;
            }
            response = await purchaseService.cancel(id, motivo.trim());
            message = 'Compra cancelada com sucesso!';
          } else {
            return;
          }
          break;
      }
      
      if (response?.success) {
        alertService.toast('success', message);
        loadPurchase(); // Recarregar para atualizar o status
      }
    } catch (error) {
      console.error('Erro ao alterar status da compra:', error);
      alertService.toast('error', 'Erro ao alterar status da compra.');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; icon: React.ReactNode; label?: string }> = {
      PENDENTE: { color: 'bg-orange-100 text-orange-700 border border-orange-200', icon: <ShoppingCart className="w-4 h-4" /> },
      CONFIRMADA: { color: 'bg-orange-100 text-orange-700 border border-orange-200', icon: <CheckCircle className="w-4 h-4" /> },
      EM_TRANSITO: { color: 'bg-blue-100 text-blue-700 border border-blue-200', icon: <Truck className="w-4 h-4" /> },
      PARCIALMENTE_ENTREGUE: { color: 'bg-yellow-100 text-yellow-700 border border-yellow-200', icon: <Truck className="w-4 h-4" /> },
      ENTREGUE: { color: 'bg-green-100 text-green-700 border border-green-200', icon: <Truck className="w-4 h-4" />, label: 'FINALIZADA' },
      FINALIZADA: { color: 'bg-green-100 text-green-700 border border-green-200', icon: <CheckCircle className="w-4 h-4" /> },
      CANCELADA: { color: 'bg-red-100 text-red-700 border border-red-200', icon: <XCircle className="w-4 h-4" /> }
    };

    const key = String(status || '').toUpperCase();
    const config = statusConfig[key] || statusConfig.PENDENTE;
    const label = config.label || key;

    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.icon}
        {label}
      </span>
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateSafe = (primary?: string, fallback?: string) => {
    const tryParse = (s?: string) => {
      if (!s) return null;
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    };
    const d = tryParse(primary) || tryParse(fallback);
    if (!d) return '-';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTimeSafe = (primary?: string, fallback?: string) => {
    const tryParse = (s?: string) => {
      if (!s) return null;
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    };
    const d = tryParse(primary) || tryParse(fallback);
    if (!d) return '-';
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-gray-500">
            <ShoppingCart className="w-6 h-6 animate-pulse" />
            <span>Carregando compra...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!purchase) {
    return (
      <div>
        <div className="text-center py-12">
          <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Compra não encontrada</h3>
          <p className="mt-1 text-sm text-gray-500">A compra solicitada não foi encontrada.</p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/compras')}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Voltar para Compras
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate('/compras')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center space-x-3 flex-1">
            <ShoppingCart className="h-8 w-8 text-red-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Detalhes da Compra</h1>
              <p className="text-gray-600">Visualize as informações da compra</p>
            </div>
          </div>
          
          {/* Ações */}
          <div className="flex items-center gap-2">
            {purchase.status === 'PENDENTE' && (
              <>
                <button
                  onClick={handleEdit}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Edit className="h-4 w-4" />
                  Editar
                </button>
                <button
                  onClick={() => handleStatusChange('confirm')}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <CheckCircle className="h-4 w-4" />
                  Confirmar
                </button>
              </>
            )}
            
            {purchase.status === 'CONFIRMADA' && (
              <button
                onClick={() => handleStatusChange('deliver')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Truck className="h-4 w-4" />
                Marcar como Entregue
              </button>
            )}
            
            {(purchase.status === 'PENDENTE' || purchase.status === 'CONFIRMADA') && (
              <button
                onClick={() => handleStatusChange('cancel')}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <XCircle className="h-4 w-4" />
                Cancelar
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informações Principais */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informações Gerais */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informações Gerais</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Fornecedor</p>
                  <p className="text-gray-900">{purchase.fornecedorNome}</p>
                  {purchase.fornecedorCnpj && (
                    <p className="text-sm text-gray-500">{purchase.fornecedorCnpj}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Data da Compra</p>
                  <p className="text-gray-900">{formatDate(purchase.dataCompra)}</p>
                </div>
              </div>

              {purchase.numeroNotaFiscal && (
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Nota Fiscal</p>
                    <p className="text-gray-900">{purchase.numeroNotaFiscal}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Package className="h-5 w-5 text-gray-400 mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  {getStatusBadge(purchase.status)}
                </div>
              </div>
            </div>

            {purchase.observacoes && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-500 mb-2">Observações</p>
                <p className="text-gray-900">{purchase.observacoes}</p>
              </div>
            )}
          </div>

          {/* Itens da Compra */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Itens da Compra</h2>
            
            <div className="space-y-4">
              {purchase.itens.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{item.produtoNome}</h3>
                      {item.produtoCodigo && (
                        <p className="text-sm text-gray-500">Código: {item.produtoCodigo}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        {formatCurrency(item.quantidade * item.precoUnitario)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Quantidade: </span>
                      <span className="text-gray-900">{item.quantidade}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Preço Unitário: </span>
                      <span className="text-gray-900">{formatCurrency(item.precoUnitario)}</span>
                    </div>
                  </div>

                  {item.observacoes && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-sm text-gray-500">Observações: {item.observacoes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Resumo Financeiro */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Resumo Financeiro</h2>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total de itens:</span>
                <span className="font-medium">{purchase.itens.length}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Quantidade total:</span>
                <span className="font-medium">
                  {purchase.itens.reduce((total, item) => total + item.quantidade, 0)}
                </span>
              </div>
              
              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">Valor Total:</span>
                  <span className="text-lg font-bold text-red-600">
                    {formatCurrency(purchase.valorTotal)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Pagamentos */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pagamentos</h2>
            {paymentsLoading ? (
              <div className="flex items-center gap-3 text-gray-500">
                <CreditCard className="w-5 h-5 animate-pulse" />
                <span>Carregando pagamentos...</span>
              </div>
            ) : payments.length === 0 ? (
              <p className="text-sm text-gray-600">Nenhum pagamento cadastrado.</p>
            ) : (
              <div className="space-y-3">
                {payments.map((p) => (
                  <div key={p.id} className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-2 h-2 rounded-full mt-2 ${
                          isPaidStatus(p.status) ? 'bg-green-500' : p.status === 'CANCELADO' ? 'bg-red-500' : 'bg-orange-500'
                        }`}
                      ></div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {p.tipoPagamentoNome || 'Pagamento'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {isPaidStatus(p.status)
                            ? formatDateTimeSafe(p.dataPagamento, p.updatedAt)
                            : formatDateTimeSafe(p.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(p.valor)}</span>
                      <div className="text-xs">
                        <span
                          className={`inline-block mt-1 px-2 py-0.5 rounded-full border text-[11px] ${
                            isPaidStatus(p.status)
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : p.status === 'CANCELADO'
                              ? 'bg-red-50 text-red-700 border-red-200'
                              : 'bg-orange-50 text-orange-700 border-orange-200'
                          }`}
                        >
                          {p.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Histórico */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Histórico</h2>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Compra criada</p>
                  <p className="text-xs text-gray-500">{formatDateTimeSafe(purchase.createdAt, purchase.dataCompra)}</p>
                </div>
              </div>
              
              {purchase.status === 'CONFIRMADA' && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Compra confirmada</p>
                    <p className="text-xs text-gray-500">{formatDateTimeSafe(purchase.updatedAt, purchase.createdAt)}</p>
                  </div>
                </div>
              )}
              
              {purchase.status === 'ENTREGUE' && purchase.dataEntrega && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Compra entregue</p>
                    <p className="text-xs text-gray-500">{formatDateSafe(purchase.dataEntrega, purchase.updatedAt)}</p>
                  </div>
                </div>
              )}
              
              {purchase.status === 'CANCELADA' && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Compra cancelada</p>
                    <p className="text-xs text-gray-500">{formatDateTimeSafe(purchase.updatedAt, purchase.createdAt)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseViewPage;